import { FC, ReactElement, memo, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import {
    RedirectsQueryKeys,
    useDeleteRedirectsMutation,
    useEditRedirectsMutation,
    useGetByIdRedirectsQuery,
} from '@/modules/Sites';

import { RedirectsOverviewView } from './RedirectsOverviewView';

interface Props {
  siteId: string;
  redirectId: string;
}

export const RedirectsOverviewContainer: FC<Props> = memo(({ siteId, redirectId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const {
    data: redirectData,
    error: redirectError,
    isLoading: isRedirectLoading,
    isError: isRedirectError,
    refetch: refetchRedirect,
  } = useGetByIdRedirectsQuery(
    siteId,
    redirectId,
  )({
    enabled: !!redirectId && !!siteId,
  });

  const { mutate: editRedirectMutation, isLoading: editRedirectsIsLoading } = useEditRedirectsMutation(
    siteId,
    redirectId,
  )({
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries([RedirectsQueryKeys.GetAll, siteId]),
        queryClient.refetchQueries([RedirectsQueryKeys.Conflicts, siteId]),
      ]);

      await refetchRedirect();

      appMessageApi.success('Редірект успішно змінено!');

      navigate(AppRoutes.RedirectsById.replace(':siteId', siteId).replace(':redirectId', redirectId));
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    },
  });

  const { isLoading: isDeleteRedirectLoading, mutate: deleteHostingMutation } = useDeleteRedirectsMutation(
    siteId,
    redirectId,
  )({
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries([RedirectsQueryKeys.GetAll, siteId]),
        queryClient.refetchQueries([RedirectsQueryKeys.Conflicts, siteId]),
      ]);

      appMessageApi.success('Редірект успішно видалено!');

      navigate(AppRoutes.Redirects.replace(':siteId', siteId));
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    },
  });

  const handleDeleteRedirect = useCallback((): void => {
    deleteHostingMutation();
  }, []);

  const handleEditRedirect = useCallback((): void => {
    navigate(AppRoutes.RedirectsEdit.replace(':siteId', siteId).replace(':redirectId', redirectId));
  }, [siteId, redirectId]);

  const handleActivateRedirect = useCallback((): void => {
    editRedirectMutation({ isActive: !redirectData?.isActive, siteId });
  }, [redirectData?.isActive]);

  return (
    <FallbackedLayout data={redirectData} isLoading={isRedirectLoading} isError={isRedirectError} error={redirectError}>
      {(redirect): ReactElement => (
        <RedirectsOverviewView
          redirect={redirect}
          disableButtons={isDeleteRedirectLoading || editRedirectsIsLoading}
          isDeleteRedirectLoading={isDeleteRedirectLoading}
          editRedirectsIsLoading={editRedirectsIsLoading}
          handleActivateRedirect={handleActivateRedirect}
          handleEditRedirect={handleEditRedirect}
          handleDeleteRedirect={handleDeleteRedirect}
        />
      )}
    </FallbackedLayout>
  );
});
